import { Panel, Label } from '@playcanvas/pcui/react';
import React from 'react';

import { SetProperty, ObserverData } from '../../types';
import { Detail } from '../components';

interface MaterialData {
    name: string;
    id: string;
    textures: Array<{
        property: string;
        textureName: string;
        textureFilename: string;
    }>;
}

class MaterialsPanel extends React.Component<{ 
    sceneData: ObserverData['scene'], 
    setProperty: SetProperty 
}> {
    shouldComponentUpdate(nextProps: Readonly<{ 
        sceneData: ObserverData['scene']; 
        setProperty: SetProperty; 
    }>): boolean {
        return (
            JSON.stringify(nextProps.sceneData.materials) !== JSON.stringify(this.props.sceneData.materials)
        );
    }

    private formatPropertyName(property: string): string {
        // Convert camelCase to readable format
        return property
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .replace(/Map$/, '');
    }

    private getTextureTypeIcon(property: string): string {
        const iconMap: { [key: string]: string } = {
            'diffuseMap': '🎨', // Color/Albedo
            'normalMap': '📐', // Normal
            'specularMap': '✨', // Specular
            'glossMap': '💎', // Gloss
            'metalnessMap': '🔩', // Metalness
            'emissiveMap': '💡', // Emissive
            'aoMap': '🌑', // Ambient Occlusion
            'heightMap': '⛰️', // Height
            'opacityMap': '👻', // Opacity
            'lightMap': '🔆' // Light
        };
        return iconMap[property] || '📄';
    }

    render() {
        const scene = this.props.sceneData;
        const materials: MaterialData[] = scene.materials || [];
        const hasMaterials = materials.length > 0;

        // Calculate texture usage statistics
        const textureUsageMap = new Map<string, { count: number; materials: string[] }>();
        materials.forEach(material => {
            material.textures.forEach(texture => {
                const usage = textureUsageMap.get(texture.textureFilename) || { count: 0, materials: [] };
                usage.count++;
                if (!usage.materials.includes(material.name)) {
                    usage.materials.push(material.name);
                }
                textureUsageMap.set(texture.textureFilename, usage);
            });
        });

        return (
            <Panel 
                headerText={`MATERIALS & TEXTURES (${materials.length})`} 
                class='scene-materials-panel' 
                enabled={hasMaterials} 
                collapsible={true}
                collapsed={materials.length > 8} // Auto-collapse if many materials
            >
                {hasMaterials && (
                    <div className='materials-content'>
                        {/* Materials Section */}
                        <Panel 
                            headerText={`Materials (${materials.length})`}
                            class='materials-list-panel'
                            collapsible={true}
                            collapsed={false}
                        >
                            <div className='materials-list'>
                                {materials.map((material, index) => (
                                    <Panel 
                                        key={material.id}
                                        headerText={material.name}
                                        class='material-item-panel'
                                        collapsible={true}
                                        collapsed={true}
                                    >
                                        <Detail label='Material ID' value={material.id} />
                                        <Detail label='Texture Count' value={material.textures.length.toString()} />
                                        
                                        {material.textures.length > 0 && (
                                            <div className='material-textures'>
                                                <Label text='Textures:' class='textures-header' />
                                                {material.textures.map((texture, texIndex) => (
                                                    <div key={`${material.id}-tex-${texIndex}`} className='texture-usage-item'>
                                                        <Label 
                                                            text={`${this.getTextureTypeIcon(texture.property)} ${this.formatPropertyName(texture.property)}: ${texture.textureFilename}`}
                                                            class='texture-usage-label'
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </Panel>
                                ))}
                            </div>
                        </Panel>

                        {/* Texture Usage Section */}
                        <Panel 
                            headerText={`Texture Usage (${textureUsageMap.size})`}
                            class='texture-usage-panel'
                            collapsible={true}
                            collapsed={true}
                        >
                            <div className='texture-usage-list'>
                                {Array.from(textureUsageMap.entries())
                                    .sort(([,a], [,b]) => b.count - a.count) // Sort by usage count descending
                                    .map(([filename, usage]) => (
                                    <Panel 
                                        key={filename}
                                        headerText={`${filename} (${usage.count} use${usage.count !== 1 ? 's' : ''})`}
                                        class='texture-usage-item-panel'
                                        collapsible={true}
                                        collapsed={true}
                                    >
                                        <Detail label='Usage Count' value={usage.count.toString()} />
                                        <Detail label='Used in Materials' value={usage.materials.join(', ')} />
                                    </Panel>
                                ))}
                            </div>
                        </Panel>
                    </div>
                )}
            </Panel>
        );
    }
}

export default MaterialsPanel; 